/**
 * What went wrong with a call, in words a person can act on. The call UI never shows a raw Error:
 * known failures map to a kind with human copy, and the precise cause only appears behind
 * "Details" (so it stays diagnosable).
 */
import { isContentLockError } from '../content-lock.js'
import type { CallBlocker } from './state.js'

const NOT_APPROVED = /approve this machine locally|approve-elsewhere|untrusted machine content key/i
const NO_MAC = /no mac for this call|none is online|no_machine|machine.*offline/i
const MIC = /microphone|notallowederror.*audio|permission denied/i

const message = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === 'string' ? e : 'Unknown error'

/** Why a call couldn't start, from whatever the start path threw. */
export function callBlockerFrom(e: unknown): CallBlocker {
  const detail = message(e)
  if (e instanceof Error && isContentLockError(e)) return { kind: 'locked', detail }
  if (NOT_APPROVED.test(detail)) return { kind: 'not-approved', detail }
  if (NO_MAC.test(detail)) return { kind: 'offline', detail }
  if (MIC.test(detail)) return { kind: 'mic', detail }
  return { kind: 'failed', detail }
}

export interface CallCopy {
  title: string
  body: string
  /** The one primary action's label. */
  action: string
}

/** Human copy for a blocked start (`mac` is the Mac's display name, if known). */
export function blockerCopy(b: CallBlocker, mac: string | undefined): CallCopy {
  const m = mac ?? 'your Mac'
  switch (b.kind) {
    case 'locked':
      return {
        title: 'Unlock to start a call',
        body: `${m} encrypts the conversation, so this browser needs your key first. One passkey tap unlocks it.`,
        action: 'Unlock and start call',
      }
    case 'not-approved':
      return {
        title: 'Unlock to start a call',
        body: `${m} encrypts the conversation, and this browser can’t confirm its key yet. Open Viko once on the browser you added ${m} from, then call again.`,
        action: 'Try again',
      }
    case 'offline':
      return {
        title: 'Your Mac is offline',
        body: `Calls run on ${m}. Wake it (with vikod running), then call again.`,
        action: 'Try again',
      }
    case 'mic':
      return {
        title: 'Viko can’t hear you',
        body: 'This browser blocked the microphone. Allow it in the site settings, or type to Viko instead.',
        action: 'Try again',
      }
    case 'failed':
      return {
        title: 'The call didn’t start',
        body: `${m} didn’t answer the call. Try again in a moment.`,
        action: 'Try again',
      }
  }
}

/** A problem during a live call (the call keeps going): one sentence, details separate. */
export function liveProblemText(raw: string): string {
  if (/^transcription failed/i.test(raw))
    return 'Your Mac couldn’t transcribe that. Say it again, or type it.'
  if (MIC.test(raw)) return 'The microphone is blocked here. Allow it, or type to Viko.'
  if (/passkey|notallowederror|not allowed/i.test(raw))
    return 'The passkey wasn’t approved. The actions are still waiting.'
  if (NOT_APPROVED.test(raw) || /content locked/i.test(raw))
    return 'This browser can’t encrypt to your Mac right now. Unlock, then try again.'
  if (/did not get that|didn’t get that/i.test(raw)) return 'Viko didn’t get that. Try again.'
  return 'Something on the call didn’t go through. Try again.'
}
